import { ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ConfirmDeleteDetail {
  label: string;
  value: ReactNode;
  emphasis?: boolean;
}

interface ConfirmDeleteDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  details?: ConfirmDeleteDetail[];
  loading?: boolean;
  error?: string | null;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Deactivating is not deleting, and the dialog has to say so before the label is read:
   * amber warns, red destroys. Defaults to the destructive tone the two existing callers use.
   */
  tone?: 'danger' | 'warning';
  onConfirm: () => void;
  onClose: () => void;
}

export const ConfirmDeleteDialog = ({
  open,
  title,
  description,
  details = [],
  loading = false,
  error = null,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  onConfirm,
  onClose,
}: ConfirmDeleteDialogProps) => {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('Delete');
  const resolvedCancelLabel = cancelLabel ?? t('Cancel');
  const danger = tone === 'danger';

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && !loading && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className={danger ? 'h-5 w-5 text-red-600' : 'h-5 w-5 text-amber-500'} />
            <AlertDialogTitle>{title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>{description}</p>
              {details.length > 0 && (
                <div className="rounded-md border bg-gray-50 p-3 text-sm space-y-1 dark:border-zinc-700 dark:bg-zinc-800/60">
                  {details.map((d, idx) => (
                    <div key={idx} className="grid grid-cols-[100px_1fr] gap-1">
                      <span className="text-gray-500 dark:text-gray-400">{d.label}</span>
                      <span className={d.emphasis ? 'font-medium' : undefined}>
                        {d.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{resolvedCancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={loading}
            className={danger ? 'bg-red-600 hover:bg-red-700' : undefined}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {resolvedConfirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
